import React, { useCallback, useEffect, useState } from 'react'

import { Xmark } from '@gravity-ui/icons'
import { Button, InputGroup } from '@heroui/react'
import { useTranslation } from 'react-i18next'

import { DriverAutocomplete } from '@/components/common/DriverAutocomplete'
import { VehicleAutocomplete } from '@/components/common/VehicleAutocomplete'
import { Card, CardContent } from '@/components/ui/Card'
import { DateRangePicker } from '@/components/ui/DateRangePicker'
import { useDebounce } from '@/hooks/useDebounce'
import type { Driver } from '@/types/driver'
import type { Vehicle } from '@/types/vehicle'

interface ReportsFiltersProps {
  dateFrom: string;
  dateTo: string;
  onDateRangeChange: (from: string, to: string) => void;
  driverID: string;
  onDriverChange: (id: string) => void;
  vehicleID: string;
  onVehicleChange: (id: string) => void;
  document: string;
  onDocumentChange: (doc: string) => void;
}

export function ReportsFilters({
                                 dateFrom,
                                 dateTo,
                                 onDateRangeChange,
                                 driverID,
                                 onDriverChange,
                                 vehicleID,
                                 onVehicleChange,
                                 document,
                                 onDocumentChange,
                               }: ReportsFiltersProps) {
  const { t } = useTranslation()

  const [docValue, setDocValue] = useState(document)
  const debouncedDocValue = useDebounce(docValue, 400)

  useEffect(() => {
    setDocValue(document)
  }, [document])

  useEffect(() => {
    if (debouncedDocValue !== document) {
      onDocumentChange(debouncedDocValue)
    }
  }, [debouncedDocValue, document, onDocumentChange])

  const rangeValue = React.useMemo(() => dateFrom && dateTo ? { start: dateFrom, end: dateTo } : null, [dateFrom, dateTo])

  const handleDateRangeChange = useCallback((val: { start: string; end: string } | null) => {
    if (val) {
      onDateRangeChange(val.start, val.end)
    } else {
      onDateRangeChange('', '')
    }
  }, [onDateRangeChange])

  const handleDriverChange = useCallback((d: Driver | null) => {
    onDriverChange(d?.ID ?? '')
  }, [onDriverChange])

  const handleVehicleChange = useCallback((v: Vehicle | null) => {
    onVehicleChange(v?.ID ?? '')
  }, [onVehicleChange])

  return (
    <Card>
      <CardContent className="p-2">
        <div className="flex gap-6 items-center flex-wrap">
          <div className="flex-1">
            <InputGroup className="w-full" variant={'secondary'}>
              <InputGroup.Input
                value={docValue}
                onChange={e => setDocValue(e.target.value)}
                placeholder={t('reports.table.doc_placeholder', 'Document')}
                className="w-full"
              />
              {docValue && (
                <InputGroup.Suffix >
                  <Button
                    isIconOnly
                    aria-label="Clear"
                    size="sm"
                    variant="ghost"
                    onClick={() => setDocValue('')}
                  >
                    <Xmark className="size-3" />
                  </Button>
                </InputGroup.Suffix>
              )}
            </InputGroup>
          </div>
          <div className="flex-1 min-w-72">
            <DateRangePicker
              value={rangeValue}
              onChange={handleDateRangeChange}
              variant={'secondary'}
            />
          </div>
          <div className="flex-1 w-64">
            <DriverAutocomplete
              selectedId={driverID}
              onSelectionChange={handleDriverChange}
            />
          </div>
          <div className="flex-1 w-64">
            <VehicleAutocomplete
              selectedId={vehicleID}
              onSelectionChange={handleVehicleChange}
            />
          </div>

        </div>
      </CardContent>
    </Card>
  )
}
